import React, { useEffect, useState, useRef } from "react";
import styles from "../../styles/Home.module.css";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectCurrentToken } from "../../features/auth/authSlice";
import TextZoom from "../../components/TextZoom";
import ComputerIcon from "@mui/icons-material/Computer";
import GavelIcon from "@mui/icons-material/Gavel";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import ArticleIcon from "@mui/icons-material/Article";
import MessageIcon from "@mui/icons-material/Message";
import InsightsIcon from "@mui/icons-material/Insights";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import BusinessIcon from "@mui/icons-material/Business";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { useTranslation } from "react-i18next";
import { optimizedPreload } from "../../utils/imagePreloader";

// Custom hook for Intersection Observer
const useIntersectionObserver = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, options);

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options]);

  return [ref, isVisible];
};

// Animated section component
const AnimatedSection = ({ children, threshold = 0.1, className, delay = 0 }) => {
  const [ref, isVisible] = useIntersectionObserver({ threshold });

  return (
    <div
      ref={ref}
      className={`${className} ${isVisible ? styles.animate : styles.hidden}`}
      style={{
        transitionDelay: `${delay}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
      }}
    >
      {children}
    </div>
  );
};

const Home = () => {
  const { t } = useTranslation(["Home"]);
  const navigate = useNavigate();
  const token = useSelector(selectCurrentToken);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    if (token !== null) navigate("/minar-sidur");

    // Add meta tags for SEO
    document.title = "Bidd - Home";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("heroDescription"));
    } else {
      const meta = document.createElement('meta');
      meta.name = "description";
      meta.content = t("heroDescription");
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Preload critical images
    const criticalImages = [
      '/images/blodrur-mynd.svg',
      '/images/blodrur-mynd-litil.svg'
    ];

    // Start preloading
    optimizedPreload(criticalImages)
      .then(() => setImagesLoaded(true))
      .catch(err => console.error('Image preloading error:', err));
  }, [navigate, token, t]);

  return (
    <>
      <Header />
      <div className={styles.mainHeroContainer}>
        <div className={styles.heroContainer}>
          <AnimatedSection className={styles.heroDetails} delay={200}>
            <h1 className={styles.heroTitle}>
              <TextZoom />
              {t("get")} <span className={styles.greenTxt}>Bidd.</span>
            </h1>
            <p className={styles.heroDescription}>{t("heroDescription")}</p>
          </AnimatedSection>
          <AnimatedSection className={styles.heroImageContainer} delay={400}>
            <div className={styles.heroImageInsideContainer}>
              <picture>
                <source
                  media="(max-width: 599px)"
                  srcSet="/images/blodrur-mynd-litil.svg"
                  width="303"
                  height="303"
                  type="image/svg+xml"
                />
                <source
                  media="(min-width: 600px)"
                  srcSet="/images/blodrur-mynd.svg"
                  width="407"
                  height="407"
                  type="image/svg+xml"
                />

                <img
                  src="/images/blodrur-mynd.svg"
                  alt="blodrur-mynd"
                  width="407"
                  height="407"
                  fetchpriority="high"
                />
              </picture>
            </div>
          </AnimatedSection>
        </div>
      </div>

      <div className={styles.contentSectionContainer}>
        <AnimatedSection className={styles.contentSection1}>
          <img
            className={styles.photoImageDesktop}
            src="/images/Bring_Solutions_To_Problem.svg"
            alt="bring_solutions_to_problems"
            width="507"
            height="507"
            loading="lazy"
          />
          <img
            className={styles.photoImageTablet}
            src="/images/Bring_Solutions_To_Problem.svg"
            alt="bring_solutions_to_problems"
            width="100%"
            height="100%"
            loading="lazy"
          />
          <div className={styles.contentSection1Details}>
            <div className={styles.contentSection1DetailsHeader}>
              <h2 className={styles.contentSection1DetailsTitle}>
                {t("section1Heading")}
              </h2>
            </div>
            <div className={styles.contentSection1DetailsFeatures}>
              <AnimatedSection className={styles.contentSection1DetailsFeature} delay={200}>
                <div
                  style={{
                    backgroundColor: "black",
                    padding: "3px 5px 3px 5px",
                    borderRadius: "50%",
                  }}
                >
                  <BusinessIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
                <div className={styles.contentSection1DetailsFeatureDetails}>
                  <h3 className={styles.contentSection1DetailsFeatureTitle}>
                    {t("section1SubHeading1")}
                  </h3>
                  <p className={styles.contentSection1DetailsFeatureDescription}>
                    {t("section1SubDescription1")}
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection className={styles.contentSection1DetailsFeature} delay={400}>
                <div
                  style={{
                    backgroundColor: "black",
                    padding: "3px 5px 3px 5px",
                    borderRadius: "50%",
                  }}
                >
                  <EngineeringIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
                <div className={styles.contentSection1DetailsFeatureDetails}>
                  <h3 className={styles.contentSection1DetailsFeatureTitle}>
                    {t("section1SubHeading2")}
                  </h3>
                  <p className={styles.contentSection1DetailsFeatureDescription}>
                    {t("section1SubDescription2")}
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection className={styles.contentSection1DetailsFeature} delay={600}>
                <div
                  style={{
                    backgroundColor: "black",
                    padding: "3px 5px 3px 5px",
                    borderRadius: "50%",
                  }}
                >
                  <AssuredWorkloadIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
                <div className={styles.contentSection1DetailsFeatureDetails}>
                  <h3 className={styles.contentSection1DetailsFeatureTitle}>
                    {t("section1SubHeading3")}
                  </h3>
                  <p className={styles.contentSection1DetailsFeatureDescription}>
                    {t("section1SubDescription3")}
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </AnimatedSection>
      </div>

      <div className={styles.featuresContainer}>
        <AnimatedSection className={styles.featuresHeader}>
          <h2 className={styles.featuresTitle}>{t("section2Heading")}</h2>
        </AnimatedSection>

        <div className={styles.features}>
          <div className={styles.featuresGrid}>
            <AnimatedSection className={styles.featureBox} delay={200}>
              <div className={styles.featureTopRow}>
                <div className={styles.featureIconContainer}>
                  <ComputerIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
              </div>
              <div className={styles.featureDetailsContainer}>
                <h3 className={styles.featureDetailsTitle}>
                  {t("section2SubHeading1")}
                </h3>
                <p className={styles.featureDetailsDescription}>
                  {t("section2SubDescription1")}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection className={styles.featureBox} delay={300}>
              <div className={styles.featureTopRow}>
                <div className={styles.featureIconContainer}>
                  <GavelIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
              </div>
              <div className={styles.featureDetailsContainer}>
                <h3 className={styles.featureDetailsTitle}>
                  {t("section2SubHeading2")}
                </h3>
                <p className={styles.featureDetailsDescription}>
                  {t("section2SubDescription2")}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection className={styles.featureBox} delay={400}>
              <div className={styles.featureTopRow}>
                <div className={styles.featureIconContainer}>
                  <AutoModeIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
              </div>
              <div className={styles.featureDetailsContainer}>
                <h3 className={styles.featureDetailsTitle}>
                  {t("section2SubHeading3")}
                </h3>
                <p className={styles.featureDetailsDescription}>
                  {t("section2SubDescription3")}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection className={styles.featureBox} delay={500}>
              <div className={styles.featureTopRow}>
                <div className={styles.featureIconContainer}>
                  <ArticleIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
              </div>
              <div className={styles.featureDetailsContainer}>
                <h3 className={styles.featureDetailsTitle}>
                  {t("section2SubHeading4")}
                </h3>
                <p className={styles.featureDetailsDescription}>
                  {t("section2SubDescription4")}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection className={styles.featureBox} delay={600}>
              <div className={styles.featureTopRow}>
                <div className={styles.featureIconContainer}>
                  <MessageIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
              </div>
              <div className={styles.featureDetailsContainer}>
                <h3 className={styles.featureDetailsTitle}>
                  {t("section2SubHeading5")}
                </h3>
                <p className={styles.featureDetailsDescription}>
                  {t("section2SubDescription5")}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection className={styles.featureBox} delay={700}>
              <div className={styles.featureTopRow}>
                <div className={styles.featureIconContainer}>
                  <InsightsIcon sx={{ color: "#78ca72", fontSize: 40 }} />
                </div>
              </div>
              <div className={styles.featureDetailsContainer}>
                <h3 className={styles.featureDetailsTitle}>
                  {t("section2SubHeading6")}
                </h3>
                <p className={styles.featureDetailsDescription}>
                  {t("section2SubDescription6")}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

            <div className={styles.contentSectionContainer}>
        <AnimatedSection className={styles.hero}>
          <div className={styles.container1}>
            <div className={styles.heroBase}>
              <div className={styles.textContainer1}>
                <div className={styles.headlineSupportingText1}>
                  <b className={styles.headline}>{t("section3Heading")}</b>
                  <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                    <div className={styles.subtitle2}>
                      {t("section3Description")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
      <Footer />
    </>
  );
};

export default Home;

